"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
    DIALOG_FOOTER_CLASS,
    DIALOG_HEADER_CLASS,
    DIALOG_ICON_CLASS,
    DIALOG_TITLE_CLASS,
    dialogPadClass,
    splitHeaderClass,
    titleLayoutClass,
} from "@/components/ui/dialog-standard"

function Dialog({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
                className
            )}
            {...props} />
    );
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean
}) {
    const hasCustomMaxWidth = typeof className === "string" && /(?:^|\s)(?:[a-z0-9_-]+:)?max-w-/.test(className);
    // grid-cols-[minmax(0,1fr)]: cột nội dung không rộng hơn khung hộp thoại. Thiếu nó, chữ một dòng dài (truncate / nowrap,
    // VD tên sản phẩm ở hộp Điều Độ & Gán Lịch Máy) kéo cả cột tràn ra ngoài khung trắng.

    return (
        <DialogPortal data-slot="dialog-portal">
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                aria-describedby={props["aria-describedby"]}
                className={cn(
                    "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid grid-cols-[minmax(0,1fr)] w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 outline-none",
                    !hasCustomMaxWidth && "sm:max-w-lg",
                    // --dlg-pad: padding thật của hộp → header chuẩn kéo sát mép (dialog-standard.ts)
                    dialogPadClass(className),
                    className
                )}
                {...props}>
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-[18px] right-4 z-10 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
                        <XIcon />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

/**
 * Header chuẩn EMS: nền trắng sát mép hộp, viền dưới, icon màu chủ đạo (tuỳ chọn) + khối tiêu đề / mô tả.
 * className: phần vị trí (sticky, z-…) áp vào khung ngoài; phần bố cục (flex-row, justify-between…) áp vào khối nội dung.
 * Header sr-only (popup toàn màn hình: xem ảnh, in) giữ nguyên, không có khung.
 */
function DialogHeader({
    className,
    icon: Icon,
    children,
    ...props
}: React.ComponentProps<"div"> & { icon?: React.ElementType }) {
    const { outer, inner } = splitHeaderClass(className);
    if (/(?:^|\s)sr-only(?:\s|$)/.test(outer)) {
        return <div data-slot="dialog-header" className={className} {...props}>{children}</div>;
    }
    return (
        <div
            data-slot="dialog-header"
            className={cn("flex items-center gap-3 pr-12 text-left", DIALOG_HEADER_CLASS, outer)}
            {...props}>
            {Icon && (
                <span className={DIALOG_ICON_CLASS} aria-hidden>
                    <Icon />
                </span>
            )}
            <div className={cn("flex min-w-0 flex-1 flex-col gap-1", inner)}>{children}</div>
        </div>
    );
}

function DialogFooter({
    className,
    showCloseButton = false,
    children,
    ...props
}: React.ComponentProps<"div"> & {
    showCloseButton?: boolean
}) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", DIALOG_FOOTER_CLASS, className)}
            {...props}>
            {children}
            {showCloseButton && (
                <DialogPrimitive.Close asChild>
                    <Button variant="outline">Close</Button>
                </DialogPrimitive.Close>
            )}
        </div>
    );
}

function DialogTitle({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn(DIALOG_TITLE_CLASS, titleLayoutClass(className))}
            {...props} />
    );
}

function DialogDescription({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn("text-muted-foreground text-sm", className)}
            {...props} />
    );
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
}
